import React from 'react'

import { Modal, type DropdownOption } from '@/components/ui'
import { Form } from '@/components/ui/form/Form'

import useAlertStatuses from './useAlertStatuses'

import type { RecordActionModalProps } from '../types'

const RecordActionModal: React.FC<RecordActionModalProps> = ({ show, onCancel, onSaveRecordAction }) => {
  const { statuses, isLoading, error } = useAlertStatuses()
  const [selectedStatus, setSelectedStatus] = React.useState<DropdownOption | null>(null)
  const [notes, setNotes] = React.useState('')
  const [isSaving, setIsSaving] = React.useState(false)
  const [saveError, setSaveError] = React.useState('')

  const statusOptions = React.useMemo<DropdownOption[]>(
    () =>
      statuses.map((status) => ({
        value: String(status.id),
        label: status.statusName,
      })),
    [statuses]
  )

  const handleCancel = () => {
    setSelectedStatus(null)
    setNotes('')
    setSaveError('')
    onCancel()
  }

  const handleSave = async () => {
    if (!selectedStatus) {
      setSaveError('Please select a status.')
      return
    }

    setIsSaving(true)
    setSaveError('')

    try {
      const result = await onSaveRecordAction({
        alertActionStatusId: Number(selectedStatus.value),
        notes: notes.trim(),
      })

      if (result) {
        setSaveError(result)
        return
      }

      handleCancel()
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      show={show}
      onHide={handleCancel}
      maxWidth="50%"
      title={'Record Action'}
      bodyContent={
        <Form
          noValidate
          className="flex h-full flex-col space-y-2"
          onSubmit={(event) => {
            event.preventDefault()
            void handleSave()
          }}
        >
          {error ? (
            <p className="text-error">{error}</p>
          ) : (
            <>
              <div className="form-control flex flex-col gap-1">
                <label className="label">
                  <span className="label-text">Select Status</span>
                </label>
                <select
                  className="select select-bordered w-full"
                  value={selectedStatus?.value || ''}
                  onChange={(event) => {
                    const selected = statusOptions.find((option) => option.value === event.target.value) || null
                    setSelectedStatus(selected)
                    if (saveError) {
                      setSaveError('')
                    }
                  }}
                  disabled={isLoading || statusOptions.length === 0}
                >
                  <option value="" disabled>
                    {isLoading ? 'Loading...' : 'Select status...'}
                  </option>
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {statusOptions.length === 0 && !isLoading ? (
                  <p className="text-sm text-base-content/70">
                    No statuses are available yet. Create one in Status Management before recording an action.
                  </p>
                ) : null}
              </div>

              <div className="form-control flex flex-col gap-1">
                <label className="label">
                  <span className="label-text">Notes</span>
                </label>
                <textarea
                  className="textarea textarea-bordered h-34 focus:outline-none focus:shadow-none focus:border-primary w-full"
                  placeholder="Add notes"
                  value={notes}
                  onChange={(event) => {
                    setNotes(event.target.value)
                    if (saveError) {
                      setSaveError('')
                    }
                  }}
                />
              </div>
              {saveError ? <p className="text-error">{saveError}</p> : null}
            </>
          )}
        </Form>
      }
      footerButtons={[
        {
          label: 'Save',
          onClick: handleSave,
          disabled: isSaving || isLoading || !selectedStatus,
          isLoading: isSaving,
          class: 'btn-primary btn-sm',
        },
        {
          label: 'Cancel',
          onClick: handleCancel,
          disabled: isSaving,
          class: 'btn-ghost btn-sm',
        },
      ]}
    />
  )
}

export default RecordActionModal
